import { useState } from 'react';
import type { AdminSigningEnvelope } from '@dataroom/shared';
import {
  useCancelEnvelope,
  useIssueEnvelope,
  useRoomActors,
  useRoomDocuments,
  useRoomEnvelopes,
  useSetRoomMasterNda,
} from '@/api/hooks';
import { AgreementDetailModal } from '@/components/AgreementDetailModal';
import { AgreementsCard } from '@/components/AgreementsCard';
import { IssueAgreementDialog } from '@/components/IssueAgreementDialog';
import { apiMessage, plainError } from '@/lib/errors';
import { useRoomContext } from './context';

/** PRD-0004 §3c — NDA/NC agreements: the master NDA, who's been issued what,
 * who has signed; issue a new one, open a row to see progress or cancel it. */
export function RoomAgreementsPage() {
  const { roomId: id, room } = useRoomContext();
  const envelopes = useRoomEnvelopes(id);
  const documents = useRoomDocuments(id);
  const actors = useRoomActors(id);
  const setMasterNda = useSetRoomMasterNda(id);
  const issueEnvelope = useIssueEnvelope(id);
  const [issueOpen, setIssueOpen] = useState(false);
  const cancelEnvelope = useCancelEnvelope(id);
  const [selectedEnvelope, setSelectedEnvelope] = useState<AdminSigningEnvelope | null>(null);

  // An NDA/NC is issued to a buyer, so the signer picker excludes the room's
  // admin (the caller) and its bound seller principal (PRD-0004 §3c).
  const signerCandidates = (actors.data ?? []).filter((a) => !a.isAdmin && !a.isSeller);

  return (
    <>
      <AgreementsCard
        envelopes={envelopes.data ?? []}
        loading={envelopes.isPending}
        error={apiMessage(envelopes.error)}
        onIssue={() => {
          issueEnvelope.reset();
          setIssueOpen(true);
        }}
        documents={documents.data ?? []}
        masterNdaDocumentId={room.masterNdaDocumentId ?? null}
        onSetMasterNda={(documentId) => setMasterNda.mutate({ documentId })}
        masterNdaPending={setMasterNda.isPending}
        masterNdaError={plainError(setMasterNda.error)}
        onSelectEnvelope={(envelope) => {
          cancelEnvelope.reset();
          setSelectedEnvelope(envelope);
        }}
      />

      {issueOpen ? (
        <IssueAgreementDialog
          actors={signerCandidates}
          documents={documents.data ?? []}
          masterNdaDocumentId={room.masterNdaDocumentId ?? null}
          pending={issueEnvelope.isPending}
          error={apiMessage(issueEnvelope.error)}
          onClose={() => {
            setIssueOpen(false);
            issueEnvelope.reset();
          }}
          onSubmit={(body) =>
            issueEnvelope.mutate(body, {
              onSuccess: () => setIssueOpen(false),
            })
          }
        />
      ) : null}

      {selectedEnvelope ? (
        <AgreementDetailModal
          envelope={selectedEnvelope}
          documents={documents.data ?? []}
          onClose={() => {
            setSelectedEnvelope(null);
            cancelEnvelope.reset();
          }}
          onCancel={() =>
            cancelEnvelope.mutate(selectedEnvelope.id, {
              onSuccess: () => setSelectedEnvelope(null),
            })
          }
          cancelPending={cancelEnvelope.isPending}
          cancelError={plainError(cancelEnvelope.error)}
        />
      ) : null}
    </>
  );
}
